import { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Phone, 
  MessageSquare, 
  Mail, 
  Save,
  ExternalLink,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

export default function SettingsPage() {
  const [isMockMode, setIsMockMode] = useState(true);
  const [providers, setProviders] = useState({
    vonage: { api_key: '', api_secret: '', enabled: true },
    messagebird: { api_key: '', enabled: true },
    whatsapp_business: { api_key: '', phone_id: '', enabled: true },
    sendgrid: { api_key: '', enabled: true },
    resend: { api_key: '', enabled: true },
    mailgun: { api_key: '', domain: '', enabled: true }
  });

  const handleSave = async () => {
    // In mock mode, just show success
    toast.success('Ayarlar kaydedildi (Mock Mod)');
  };

  const providerInfo = {
    vonage: {
      name: 'Vonage (Nexmo)',
      icon: Phone,
      description: 'Sesli arama ve SMS için',
      channels: ['voice', 'sms', 'whatsapp'],
      docsUrl: 'https://dashboard.nexmo.com/sign-up',
      color: 'bg-[#0F172A]'
    },
    messagebird: {
      name: 'MessageBird',
      icon: MessageSquare,
      description: 'WhatsApp mesajlaşma için',
      channels: ['whatsapp'],
      docsUrl: 'https://dashboard.messagebird.com/en/sign-up',
      color: 'bg-blue-600'
    },
    whatsapp_business: {
      name: 'WhatsApp Business API',
      icon: MessageSquare,
      description: 'Doğrudan WhatsApp entegrasyonu',
      channels: ['whatsapp'],
      docsUrl: 'https://business.whatsapp.com/products/business-platform',
      color: 'bg-green-600'
    },
    sendgrid: {
      name: 'SendGrid',
      icon: Mail,
      description: 'E-posta gönderimi için',
      channels: ['email'],
      docsUrl: 'https://signup.sendgrid.com/',
      color: 'bg-blue-500'
    },
    resend: {
      name: 'Resend',
      icon: Mail,
      description: 'Modern e-posta API',
      channels: ['email'],
      docsUrl: 'https://resend.com/signup',
      color: 'bg-zinc-900'
    },
    mailgun: {
      name: 'Mailgun',
      icon: Mail,
      description: 'E-posta altyapısı',
      channels: ['email'],
      docsUrl: 'https://signup.mailgun.com/new/signup',
      color: 'bg-rose-600'
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Ayarlar</h1>
        <p className="text-muted-foreground">Provider API anahtarlarını ve sistem ayarlarını yönetin</p>
      </div>

      {/* Mock Mode Banner */}
      <Card className="border-[#F97316] bg-[#F97316]/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#F97316]/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[#F97316]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#F97316]">MOCK MODU AKTİF</h3>
                <p className="text-sm text-muted-foreground">
                  Gerçek API çağrıları yapılmıyor. Test için simüle edilmiş yanıtlar kullanılıyor.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="mock-mode" className="text-sm">Mock Modu</Label>
              <Switch
                id="mock-mode"
                checked={isMockMode}
                onCheckedChange={setIsMockMode}
                data-testid="mock-mode-switch"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Voice & SMS Providers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Sesli Arama & SMS
          </CardTitle>
          <CardDescription>
            Sesli arama ve SMS gönderimi için Vonage kullanılıyor
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${providerInfo.vonage.color} flex items-center justify-center`}>
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium">{providerInfo.vonage.name}</h4>
                  <p className="text-sm text-muted-foreground">{providerInfo.vonage.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isMockMode ? (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200">Mock</Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Aktif</Badge>
                )}
                <Switch
                  checked={providers.vonage.enabled}
                  onCheckedChange={(val) => setProviders({...providers, vonage: {...providers.vonage, enabled: val}})}
                  data-testid="vonage-switch"
                />
              </div>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="vonage-key">API Key</Label>
                <Input
                  id="vonage-key"
                  type="password"
                  placeholder="••••••••"
                  value={providers.vonage.api_key}
                  onChange={(e) => setProviders({...providers, vonage: {...providers.vonage, api_key: e.target.value}})}
                  disabled={isMockMode}
                  data-testid="vonage-api-key-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vonage-secret">API Secret</Label>
                <Input
                  id="vonage-secret"
                  type="password"
                  placeholder="••••••••"
                  value={providers.vonage.api_secret}
                  onChange={(e) => setProviders({...providers, vonage: {...providers.vonage, api_secret: e.target.value}})}
                  disabled={isMockMode}
                  data-testid="vonage-api-secret-input"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <a 
                href={providerInfo.vonage.docsUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              >
                API anahtarı al <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp Providers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            WhatsApp Providers
          </CardTitle>
          <CardDescription>
            WhatsApp mesajları için kullanılan provider'lar (round-robin)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {['messagebird', 'whatsapp_business'].map((key) => (
            <div key={key} className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${providerInfo[key].color} flex items-center justify-center`}>
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium">{providerInfo[key].name}</h4>
                    <p className="text-sm text-muted-foreground">{providerInfo[key].description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isMockMode ? (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">Mock</Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Aktif</Badge>
                  )}
                  <Switch
                    checked={providers[key].enabled}
                    onCheckedChange={(val) => setProviders({...providers, [key]: {...providers[key], enabled: val}})}
                    data-testid={`${key}-switch`}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`${key}-key`}>API Key</Label>
                <Input
                  id={`${key}-key`}
                  type="password"
                  placeholder="••••••••"
                  value={providers[key].api_key}
                  onChange={(e) => setProviders({...providers, [key]: {...providers[key], api_key: e.target.value}})}
                  disabled={isMockMode}
                  data-testid={`${key}-api-key-input`}
                />
              </div>
              
              <div className="mt-4">
                <a 
                  href={providerInfo[key].docsUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  API anahtarı al <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Email Providers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Providers
          </CardTitle>
          <CardDescription>
            E-posta gönderimi için kullanılan provider'lar (round-robin)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {['sendgrid', 'resend', 'mailgun'].map((key) => (
            <div key={key} className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${providerInfo[key].color} flex items-center justify-center`}>
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium">{providerInfo[key].name}</h4>
                    <p className="text-sm text-muted-foreground">{providerInfo[key].description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isMockMode ? (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">Mock</Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Aktif</Badge>
                  )}
                  <Switch
                    checked={providers[key].enabled}
                    onCheckedChange={(val) => setProviders({...providers, [key]: {...providers[key], enabled: val}})}
                    data-testid={`${key}-switch`}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`${key}-key`}>API Key</Label>
                <Input
                  id={`${key}-key`}
                  type="password"
                  placeholder="••••••••"
                  value={providers[key].api_key}
                  onChange={(e) => setProviders({...providers, [key]: {...providers[key], api_key: e.target.value}})}
                  disabled={isMockMode}
                  data-testid={`${key}-api-key-input`}
                />
              </div>
              
              <div className="mt-4">
                <a 
                  href={providerInfo[key].docsUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  API anahtarı al <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isMockMode} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          Ayarları Kaydet
        </Button>
      </div>

      {/* API Keys Info */}
      <Card className="bg-secondary/30">
        <CardHeader>
          <CardTitle className="text-base">API Anahtarlarını Nereden Alırım?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.entries(providerInfo).map(([key, info]) => (
              <a
                key={key}
                href={info.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border bg-background hover:bg-secondary/50 transition-colors"
              >
                <div className={`w-8 h-8 rounded ${info.color} flex items-center justify-center`}>
                  <info.icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{info.name}</p>
                  <p className="text-xs text-muted-foreground">Ücretsiz hesap aç</p>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </a>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
